import { Link } from 'react-router-dom';
import { useState } from 'react';
import type { DemandItem } from '@/services/api';
import { formatFieldValue } from '@/components/feature/GreenCertFieldConfig';

interface DemandSummaryProps {
  items: DemandItem[];
}

export default function DemandSummary({ items }: DemandSummaryProps) {
  const activeItems = items.filter((d) => d.status === 'active').slice(0, 4);
  const [activeIndex, setActiveIndex] = useState(0);

  const currentDemand = activeItems[activeIndex] || activeItems[0];

  const next = () => {
    setActiveIndex((prev) => (prev + 1) % activeItems.length);
  };

  const prev = () => {
    setActiveIndex((prev) => (prev - 1 + activeItems.length) % activeItems.length);
  };

  if (activeItems.length === 0) {
    return null;
  }

  const projectLabel = currentDemand
    ? formatFieldValue('projectType', currentDemand.projectType)
    : '';

  return (
    <section className="w-full bg-background-50 py-16 md:py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-0">
          {/* Image area - stacks on top for mobile */}
          <div className="lg:w-2/5 relative z-10">
            <div className="relative rounded-lg overflow-hidden h-[240px] sm:h-[300px] lg:h-full">
              <img
                src="/images/demand.jpg"
                alt="采购企业"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          {/* Content area */}
          <div className="lg:w-3/5 bg-foreground-900 rounded-lg p-6 sm:p-8 md:p-12 lg:p-14 relative overflow-hidden -mt-3 lg:mt-0 lg:-ml-6">
            <div className="text-background-100/8 text-[120px] sm:text-[180px] leading-none font-heading absolute -top-6 left-4 select-none hidden sm:block">
              &ldquo;
            </div>

            <div className="relative z-10 sm:pt-8">
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={prev}
                  className="w-8 h-8 rounded-full border border-background-100/20 flex items-center justify-center text-background-100/60 hover:text-background-50 hover:border-background-100/40 transition-colors cursor-pointer"
                  aria-label="Previous"
                >
                  <i className="ri-arrow-left-line text-sm"></i>
                </button>
                <span className="text-background-100/40 text-xs font-label">
                  {activeIndex + 1} / {activeItems.length}
                </span>
                <button
                  onClick={next}
                  className="w-8 h-8 rounded-full border border-background-100/20 flex items-center justify-center text-background-100/60 hover:text-background-50 hover:border-background-100/40 transition-colors cursor-pointer"
                  aria-label="Next"
                >
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>

              {currentDemand && (
                <div className="min-h-[140px] sm:min-h-[180px]">
                  <p className="text-background-50 text-base sm:text-lg md:text-xl leading-relaxed">
                    {currentDemand.company} — {currentDemand.region}
                  </p>
                  <p className="text-background-100/70 text-sm sm:text-base md:text-lg mt-2 sm:mt-3 leading-relaxed">
                    项目类型：{projectLabel} | 采购量：{currentDemand.quantity.toLocaleString()}个
                  </p>
                  <p className="text-background-100/50 text-xs sm:text-sm mt-2 sm:mt-3 leading-relaxed">
                    预算：&yen;{currentDemand.price}/个
                  </p>
                  <p className="text-background-100/50 text-xs sm:text-sm mt-1 leading-relaxed">
                    {currentDemand.description || '暂无详细说明'}
                  </p>
                </div>
              )}

              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <p className="text-background-50 text-sm font-medium">
                    {currentDemand.company}
                  </p>
                  <p className="text-background-100/40 text-xs mt-0.5">
                    发布于 {currentDemand.publishDate}
                  </p>
                </div>
                <Link
                  to="/demand"
                  className="px-5 sm:px-6 py-2.5 bg-accent-500 text-background-50 text-sm font-medium rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  查看全部采购需求
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}